// src/modules/ACL/hooks/useAcl.js
import { useSelector } from 'react-redux'

/**
 * Custom hook for accessing ACL (Access Control List) data.
 * Provides roles, permissions, menus, and utility functions to check access.
 */
export const useAcl = () => {
  // Select roles, permissions, and menus from the Redux ACL slice
  const { roles, permissions, menus } = useSelector(
    state => state.acl
  )

  // Determine if the user is an admin based on their roles
  const isAdmin = roles.includes('admin')

  /**
   * Check if the user can view a resource (route or UI element)
   * @param {string} resourcePath - Path or identifier of the resource
   * @returns {boolean}
   */
  const canView = (resourcePath) => {
    if (isAdmin) return true
    return permissions.some(p => p.path === resourcePath && p.canView)
  }

  /**
   * Check if the user can edit a resource
   * @param {string} resourcePath - Path or identifier of the resource
   * @returns {boolean}
   */
  const canEdit = (resourcePath) => {
    if (isAdmin) return true
    return permissions.some(p => p.path === resourcePath && p.canEdit)
  }

  /**
   * Get menus filtered based on the user's view permissions
   * Admins get all menus
   * @returns {Array} - Array of menu objects
   */
  const getMenus = () => {
    if (isAdmin) return menus
    return menus.filter(m => canView(m.path))
  }

  // Return roles, permissions, menus, and utility functions
  return {
    roles,
    permissions,
    menus,
    getMenus,
    isAdmin,
    canView,
    canEdit,
  }
}
